import { Checkbox, Loader, Message, Table } from "semantic-ui-react"
import TableUpdatesRepo from "../../../../../utils/repository/tableUpdatesRepo";
import { useEffect, useMemo, useState } from "react";
import UserRepo from "../../../../../utils/repository/userRepo";
import { ROLES } from "../../../../../constnats/user";
import CollectionUtils from "../../../../../utils/collections";
import AuthService from "../../../../../service/auth";
import { adjustedUserGroupedMistakeKPIs, getRawUserGroupedMistakeKPIs } from "../../utils/qualityUtils";
import useDateRange from "../../../../../hooks/useDateRange";
import { subDays } from "date-fns";
import RangeDatePicker from "../../../../../components/rangeDatePicker";
import { getTableTypes, getQuantityPerformance, getTableData } from "./utils";
import { DEFAULT_CONFIG, CD_REVIEW_WEIGHT, ConfigProps } from "./constants";
import QuantityPerformanceConfig from "./components/quantityPerformanceConfig";

function ProductivityKPIs({ usersMap, from, to }) {
    const [performance, setPerformance] = useState({ updates: [], reviews: [], qaEffectiveness: [] })
    const [allTableTypes, setAllTableTypes] = useState([])
    const [config, setConfig] = useState(DEFAULT_CONFIG)

    useEffect(() => {
        TableUpdatesRepo.performanceKpisV2({
            from, to,
            includeDataWithUnknownMistakes: true
        }, (performanceKpis) => {

            const tableTypesArr = getTableTypes(performanceKpis)
            setAllTableTypes(tableTypesArr)
            setConfig({ ...config, tableTypes: tableTypesArr })
            setPerformance(performanceKpis)
        })

    }, [from, to]);


    const userToMistakesKPIs = getRawUserGroupedMistakeKPIs(Object.keys(usersMap), performance, config[ConfigProps.TABLE_TYPES], config[ConfigProps.MISTAKE_DETECTION_CD_MULTIPLIER])
    const mistakeAjustedKPIs = CollectionUtils.arrToMap(adjustedUserGroupedMistakeKPIs(userToMistakesKPIs), userKips => userKips.userId)
    const userToPerformanceKPIs = getQuantityPerformance(performance, usersMap, config, mistakeAjustedKPIs)
    const tableData = getTableData(userToPerformanceKPIs)

    return <>
        <br /><br />
        {AuthService.hasAnyGlobalRole([ROLES.ADMIN]) && 
            <Message error>
                    Apply a Mistake Detection Multiplier of 1.00 before 16/03/2026. For the period 16/03/2026 to 27/03/2026 inclusive, apply 1.15 (note that the data for this period is approximate and not fully accurate). Apply 1.93 after 27/03/2026.
            </Message>
        }
        <QuantityPerformanceConfig
            config={config}
            tableTypes={allTableTypes}
            onChange={setConfig} />
        <Table striped selectable celled>
            <Table.Header>
                <Table.Row >
                    <Table.HeaderCell rowSpan='2'>
                        User
                    </Table.HeaderCell>
                    <Table.HeaderCell rowSpan='2'>
                        Updates Score
                    </Table.HeaderCell>
                    <Table.HeaderCell colSpan='4'>
                        Reviews Score
                    </Table.HeaderCell>
                    <Table.HeaderCell rowSpan='2'>
                        Output
                    </Table.HeaderCell>
                    <Table.HeaderCell rowSpan='2'>
                        Total Score
                    </Table.HeaderCell>
                    <Table.HeaderCell rowSpan='2'>
                        Total Score M. Adjusted
                    </Table.HeaderCell>
                </Table.Row>
                <Table.Row >
                    <Table.HeaderCell>Full Checks</Table.HeaderCell>
                    <Table.HeaderCell>Core Data Checks (Mistakes Detection)</Table.HeaderCell>
                    <Table.HeaderCell>Core Data Checks (Regular Update)</Table.HeaderCell>
                    <Table.HeaderCell>Visual Checks</Table.HeaderCell>
                </Table.Row>
            </Table.Header>
            <Table.Body>
                {tableData
                    .map(({
                        userId, regularUpdates, mistakesDetectionUpdates, fullReviews, coreDataReviews, visualChecks,
                        totalScore, totalScoreMistakesAdjusted, incrementalCoreDataReviews }) =>
                        <Table.Row key={userId}>
                            <Table.Cell>
                                {usersMap[userId]?.fullName || userId}
                            </Table.Cell>
                            <Table.Cell>
                                <NumberCell number={regularUpdates + mistakesDetectionUpdates} />
                            </Table.Cell>
                            <Table.Cell >
                                <NumberCell number={fullReviews} />
                            </Table.Cell>
                            <Table.Cell >
                                <NumberCell number={coreDataReviews} />
                            </Table.Cell>
                            <Table.Cell >
                                <NumberCell number={incrementalCoreDataReviews} />
                            </Table.Cell>
                            <Table.Cell>
                                <NumberCell number={visualChecks} />
                            </Table.Cell>
                            <Table.Cell>
                                <NumberCell number={(coreDataReviews * CD_REVIEW_WEIGHT) + regularUpdates + mistakesDetectionUpdates} />
                            </Table.Cell>
                            <Table.Cell>
                                <NumberCell number={totalScore} />
                            </Table.Cell>
                            <Table.Cell>
                                <NumberCell number={totalScoreMistakesAdjusted} />
                            </Table.Cell>
                        </Table.Row>
                    )
                }
            </Table.Body>
        </Table>
    </>
}

function NumberCell({ number }) {
    return Number(number).toFixed(0)
}


const allowedUserIds = [2495, 2491, 997797, 2485]


function ProductivityMetrix() {
    const [includeDisabledUsers, setIncludeDisabledUsers] = useState(false);
    const [usersMap, setUsersMap] = useState(null)
    const { from, to, handleFromChange, handleToChange, handleMoveToNextMonth, handleMoveToPrevMonth } = useDateRange({ from: subDays(new Date(), 14), to: new Date() })

    const fromTM = useMemo(() => new Date(from.getTime()).setUTCHours(0, 0, 0, 0), [from])
    const toTM = useMemo(() => new Date(to.getTime()).setUTCHours(23, 59, 0, 0), [to])

    useEffect(() => {
        UserRepo.list({ userRoles: [ROLES.EMPLOYEE], includeDisabled: includeDisabledUsers },
            users => setUsersMap(CollectionUtils.arrToMap(users, u => u.id)))
    }, [includeDisabledUsers]);
    const currentUserId = AuthService.getUserId()

    if (!AuthService.hasGlobalRole(ROLES.ADMIN) && !allowedUserIds.includes(currentUserId)) {
        return <>Not enoug privileges!</>
    }

    return (
        usersMap === null ? <Loader /> :
            <>
                <Checkbox label="Include disabled users" checked={includeDisabledUsers} onChange={() => setIncludeDisabledUsers(prev => !prev)} />
                <p>NOTE: Dates before 13 Mar. 2024 are not supported</p>
                <RangeDatePicker from={from} to={to}
                    handleFromChange={handleFromChange} handleToChange={handleToChange}
                    handleMoveToNextMonth={handleMoveToNextMonth} handleMoveToPrevMonth={handleMoveToPrevMonth} />
                <ProductivityKPIs {...{ usersMap, from: fromTM, to: toTM }} />
            </>
    )
}

export default ProductivityMetrix


